import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SaveButton } from "ui-components/Form";
import { Layout } from "ui-components/Layout";
import { Success, Error_ } from "ui-components/Alert";
import { getLocationGroup, editLocationGroup } from "services/data.internal.service";
import { useLocation } from "react-router-dom";

import { formRender, initialValues, locationGroupValidations } from "./location-group-schema";
import LocationGroupInfo from "./components/LocationGroupInfo";

function EditLocationGroup() {
  const location = useLocation();
  const { locDimGrp } = location.state;
  const [alert, setAlert] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  useQuery({
    queryKey: ["location-group", { locDimGrp }],
    queryFn: () => getLocationGroup({ locDimGrp }),
    onSuccess: (data) => setFormValues(data.data.data),
    onError,
  });
  const editLocationGroupMutation = useMutation(editLocationGroup, {
    onSuccess: (data) => setAlert(<Success data={data} onChange={setAlert} />),
    onError,
  });
  const form = useForm({
    defaultValues: initialValues,
    values: formValues,
    resolver: yupResolver(locationGroupValidations),
  });

  const {
    getValues,
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
  } = form;

  const infoProps = {
    mode: "EDIT",
    getValues,
    control,
    errors,
    setValue,
    formRender,
  };
  const onSubmit = (data) => {
    let invalidCount = 0;
    if (data.grossDepth <= 0 || data.grossHeight <= 0 || data.grossWidth <= 0){
      if ((data.grossDepth > 0 !== data.grossHeight > 0) || (data.grossHeight > 0 !== data.grossWidth > 0)){
        setAlert(<Error_ content="Invalid dimension, Gross Weight, Gross Height and Gross Width are required" onChange={setAlert} />);
        return;
      } else invalidCount++;
    }
    if (data.volumeCBM <= 0 || !Boolean(data.volumeUOM)){
      if (data.volumeCBM > 0 && !Boolean(data.volumeUOM)){
        setAlert(<Error_ content="Volume CBM must have Volume UOM" onChange={setAlert} />);
        return;
      } else invalidCount++;
    }
    if (data.maxWeight <= 0 || !Boolean(data.weightUOM)){
      if (data.maxWeight > 0 && !Boolean(data.weightUOM)){
        setAlert(<Error_ content="Max Weight must have Weight UOM" onChange={setAlert} />);
        return;
      } else invalidCount++;
    }
    if (invalidCount >= 3){
      setAlert(<Error_ content="Location Dimension Group must have at least one dimension, volume or weight" onChange={setAlert} />);
      return;
    }
    editLocationGroupMutation.mutate({ locDimGrp, req: data }, {
      onSuccess: () => queryClient.invalidateQueries(["location-group"])
    });
  };
  return (
    <Layout>
      {alert}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LocationGroupInfo {...infoProps} />
        <SaveButton label="Save" disabled={!isDirty} url="/setup/location-group" />
      </form>
    </Layout>
  );
}

export default EditLocationGroup;
